"use client";
import { useState} from "react";
import Breadcrumb from "@/components/Breadcrumb";
import { ChevronDown, MessageSquare, ArrowRight } from "lucide-react";
import { Link as I18nLink } from "@/i18n/routing";
import { Helmet } from "react-helmet-async";
import { SITE_URL, normalizeLocale, sitePageUrl, ogLocaleTag } from "@/lib/site";

const faqs = [
  {
    question: "1. What digital services does Highland Group offer?",
    answer:
      "Highland Group provides custom digital solutions for businesses in Switzerland and across Europe. Our services include website development, hotel direct booking solutions, mobile app development, AI automation, business workflow systems, SaaS product development, eCommerce development, and digital growth support. We focus on practical solutions that help businesses improve efficiency, visibility, and revenue.",
  },
  {
    question: "2. What are AI automation services for businesses?",
    answer:
      "AI automation services help businesses reduce repetitive manual work and improve speed, accuracy, and efficiency. These solutions can automate customer support, lead handling, appointment or booking workflows, internal approvals, reporting, and communication. For growing businesses, AI automation helps save time, reduce costs, and improve the customer experience.",
  },
  {
    question: "3. How can AI automation help a business grow?",
    answer:
      "AI automation helps businesses grow by saving time, reducing operational delays, and improving how teams handle everyday tasks. It can support faster customer responses, better lead management, smoother workflows, and more efficient internal processes. This allows business owners and teams to focus more on growth, sales, and strategy instead of repetitive tasks.",
  },
  {
    question: "4. Do you provide custom AI solutions in Switzerland?",
    answer:
      "Yes. Highland Group provides custom AI solutions in Switzerland for businesses that need more than basic off-the-shelf tools. We build AI systems that match your business processes, goals, and customer journey. This can include AI chatbots, automated support systems, internal assistants, workflow automation, and custom AI-powered business tools.",
  },
  {
    question: "5. What is the difference between custom AI solutions and off-the-shelf AI tools?",
    answer:
      "Off-the-shelf AI tools are usually designed for general use and may work for simple tasks, but they often have limitations when it comes to integrations, workflows, and business-specific needs. Custom AI solutions are built around your exact requirements, making them more flexible, scalable, and aligned with your operations. They are often the better choice for businesses that want long-term value and better control.",
  },
  {
    question: "6. How much do AI development services cost?",
    answer:
      "The cost of AI development services depends on the type of solution, required features, integrations, and overall complexity of the project. A simple automation workflow is more affordable than a fully custom AI platform or business assistant. We usually begin by understanding your goals and then recommend a scope that fits your budget and expected return on investment.",
  },
  {
    question: "7. Do you build SEO-friendly business websites?",
    answer:
      "Yes. We build SEO-friendly business websites designed to perform well in search engines while also delivering a strong user experience. Our websites are structured for speed, mobile responsiveness, clear content hierarchy, and conversion-focused design. The goal is not only to create a professional website, but also to support better visibility and better results.",
  },
  {
    question: "8. How much does website development cost in Switzerland?",
    answer:
      "Website development costs in Switzerland vary depending on the size of the project, design quality, features, multilingual requirements, integrations, and custom functionality. A basic company website typically costs less than a custom platform with booking systems, eCommerce, or advanced user features. We create websites based on business goals, not just design preferences, so the final solution is aligned with performance and growth.",
  },
  {
    question: "9. How long does it take to build a professional website?",
    answer:
      "The timeline depends on the size and complexity of the website. A standard business website usually takes less time than a custom platform with advanced features, booking functionality, or multilingual content. The process generally includes planning, design, development, testing, and launch. We always aim to deliver a smooth and efficient process while keeping quality high.",
  },
  {
    question: "10. Can you redesign an existing website?",
    answer:
      "Yes. We can redesign existing websites to improve appearance, user experience, performance, and conversion results. A redesign can also help fix outdated layouts, poor mobile responsiveness, slow loading speed, and weak SEO structure. Our goal is to create a stronger digital presence that better reflects your brand and supports business growth.",
  },
  {
    question: "11. Do you create multilingual websites for Swiss businesses?",
    answer:
      "Yes. We build multilingual websites for businesses that need to serve different language audiences across Switzerland and Europe. Multilingual websites can improve accessibility, trust, and market reach, especially for companies working with local and international customers. We make sure the website structure supports both usability and SEO.",
  },
  {
    question: "12. What are hotel digital solutions?",
    answer:
      "Hotel digital solutions are technology services designed to help hotels, resorts, and hospitality businesses improve their online presence, direct bookings, and guest experience. These solutions can include hotel websites, booking-focused landing pages, automation tools, digital marketing support, and custom systems for hospitality operations. The goal is to help hotels grow while reducing reliance on third-party platforms.",
  },
  {
    question: "13. How can hotels increase direct bookings through their website?",
    answer:
      "Hotels can increase direct bookings by using a professional website with a clear booking journey, trust-building design, fast performance, mobile-friendly pages, and strong calls to action. A booking-focused website makes it easier for guests to find information and complete reservations directly. Combined with SEO and digital strategy, this can help hotels reduce dependence on OTAs and improve profitability.",
  },
  {
    question: "14. Do you provide hotel IT solutions in Switzerland?",
    answer:
      "Yes. Highland Group provides hotel IT solutions in Switzerland for hospitality businesses that want to improve digital performance and operational efficiency. We support hotels with website development, direct booking solutions, digital consulting, automation opportunities, and custom technology systems. Our focus is on helping hotels create better guest experiences while improving business results.",
  },
  {
    question: "15. Can a hotel booking system help reduce OTA dependence?",
    answer:
      "Yes. A well-designed hotel booking system can make direct reservations easier and more attractive for guests. When the booking process is clear, fast, and trustworthy, more visitors are likely to book directly instead of using third-party platforms. This helps hotels improve margin, strengthen customer relationships, and keep more control over the guest journey.",
  },
  {
    question: "16. Do you develop mobile apps for businesses?",
    answer:
      "Yes. We develop custom mobile apps for businesses that want to improve customer engagement, service delivery, bookings, or internal operations. Mobile apps can be built for different business goals, from customer-facing experiences to internal tools for staff and management. Every project is tailored to the business model and long-term growth strategy.",
  },
  {
    question: "17. Do you offer custom software and SaaS development?",
    answer:
      "Yes. We build custom software and SaaS products for startups, service businesses, and growing companies. This includes MVP development, product planning, interface design, backend systems, and scalable feature development. Our focus is on creating software that is practical, user-friendly, reliable, and ready to support long-term business growth.",
  },
  {
    question: "18. What is included in SaaS product development?",
    answer:
      "SaaS product development usually includes product strategy, user flow planning, UI/UX design, development, testing, launch preparation, and future improvements. Depending on the project, it may also include dashboards, subscription systems, admin panels, user roles, automation, and integrations. We help businesses turn ideas into functional digital products with a clear roadmap.",
  },
  {
    question: "19. Do you build eCommerce websites for businesses?",
    answer:
      "Yes. We build eCommerce websites that are designed for performance, user experience, and conversion. Depending on your business needs, this can include product pages, payment integrations, inventory workflows, mobile optimization, and SEO-friendly structure. Our goal is to create online stores that are easy to manage and built for long-term sales growth.",
  },
  {
    question: "20. Why choose Highland Group as your digital technology partner?",
    answer:
      "Highland Group combines strategy, design, development, and business-focused technology solutions in one place. Instead of offering generic packages, we create tailored digital systems that match your goals, industry, and growth stage. We work with businesses that want reliable execution, practical solutions, and digital tools that deliver real business value.",
  },
];

export default function FAQPage({ params }) {
  const locale = normalizeLocale(params?.locale);
  const canonical = sitePageUrl(locale, "faq");
  const homeUrl = sitePageUrl(locale);
  const orgId = `${SITE_URL}/#organization`;

  const [openIndex, setOpenIndex] = useState(0);

  const breadcrumbItems = [
    { label: "Home", href: "/" },
    { label: "FAQs" },
  ];

  const faqSchema = {
    "@context": "https://schema.org",
    "@graph": [
      {
        "@type": "FAQPage",
        "@id": `${canonical}#faqpage`,
        url: canonical,
        name: "FAQ | AI Automation & Software Services",
        description:
          "Get answers about AI automation services, software development, pricing, timelines, SaaS projects, and hotel digital solutions.",
        inLanguage: locale,
        mainEntity: faqs.map((item) => ({
          "@type": "Question",
          name: item.question.replace(/^\d+\.\s*/, ""),
          acceptedAnswer: {
            "@type": "Answer",
            text: item.answer,
          },
        })),
      },
      {
        "@type": "BreadcrumbList",
        "@id": `${canonical}#breadcrumb`,
        itemListElement: [
          { "@type": "ListItem", position: 1, name: "Home", item: homeUrl },
          { "@type": "ListItem", position: 2, name: "FAQs", item: canonical },
        ],
      },
      {
        "@type": "Organization",
        "@id": orgId,
        name: "Highlandgroup",
        url: `${SITE_URL}/`,
        logo: {
          "@type": "ImageObject",
          url: `${SITE_URL}/logo.png`,
        },
      },
    ],
  };

  return (
    <>
      <Helmet>
        <title>FAQ | AI Automation & Software Services</title>
        <meta
          name="description"
          content="Get answers about AI automation services, software development, pricing, timelines, SaaS projects, and hotel digital solutions."
        />
        <meta
          name="keywords"
          content="AI automation services FAQ, software development FAQ, SaaS development cost Europe"
        />
        <meta name="robots" content="index, follow" />
        <link rel="canonical" href={canonical} />
        <meta property="og:title" content="Highlandgroup FAQ" />
        <meta
          property="og:description"
          content="Answers to common questions about AI, software, SaaS, websites, and hotel digital solutions."
        />
        <meta property="og:type" content="website" />
        <meta property="og:url" content={canonical} />
        <meta property="og:locale" content={ogLocaleTag(locale)} />
        <meta property="og:site_name" content="Highlandgroup" />
        <meta property="og:image" content={`${SITE_URL}/logo.png`} />
        <meta name="twitter:card" content="summary_large_image" />
        <meta name="twitter:title" content="Highlandgroup FAQ" />
        <meta
          name="twitter:description"
          content="Answers to common questions about AI, software, SaaS, websites, and hotel digital solutions."
        />
        <meta name="twitter:image" content={`${SITE_URL}/logo.png`} />
        <script type="application/ld+json">{JSON.stringify(faqSchema)}</script>
      </Helmet>

      <main className="min-h-screen bg-bg-main text-charcoal selection:bg-accent-red/30">
        {/* Hero */}
        <Breadcrumb
          items={breadcrumbItems}
          title="FAQS"
          subtitle="Highland Group helps businesses grow with websites,  AI automation, mobile apps, and digital marketing. "
          description="We work with hotels, tourism companies, and service-based businesses that want stronger digital presence, better customer experience, and smarter systems for growth."
        />

        {/* FAQ list */}
        <section className="py-16 bg-bg-main">
          <div className="container mx-auto  px-6">
            <div className="grid gap-4">
              {faqs.map((item, index) => (
                <div
                  key={index}
                  className={`group rounded-2xl border transition-all duration-500 ${
                    openIndex === index
                      ? "border-accent-red/40 bg-bg-soft shadow-md"
                      : "border-border-light bg-bg-main hover:border-border-light hover:bg-bg-soft"
                  }`}
                >
                  <button
                    onClick={() =>
                      setOpenIndex(openIndex === index ? null : index)
                    }
                    className="w-full flex items-center justify-between p-6 text-left"
                  >
                    <span
                      className={`text-lg font-semibold transition-colors duration-300 ${
                        openIndex === index
                          ? "text-charcoal"
                          : "text-muted group-hover:text-charcoal"
                      }`}
                    >
                      {item.question}
                    </span>
                    <div
                      className={`transition-transform duration-500 ${
                        openIndex === index ? "rotate-180" : ""
                      }`}
                    >
                      <ChevronDown
                        className={`w-5 h-5 ${
                          openIndex === index
                            ? "text-accent-red"
                            : "text-muted"
                        }`}
                      />
                    </div>
                  </button>

                  <div
                    className={`grid transition-[grid-template-rows] duration-300 ${
                      openIndex === index
                        ? "grid-rows-[1fr] opacity-100"
                        : "grid-rows-[0fr] opacity-0"
                    }`}
                  >
                    <div className="overflow-hidden">
                      <div className="px-6 pb-6 text-muted leading-relaxed text-[0.95rem]">
                        <div className="pt-2 border-t border-border-light">
                          {item.answer}
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </section>

        {/* Bottom CTA */}
        <section className="pb-12 container mx-auto max-w-7xl px-6">
          <div className="group relative rounded-[1.5rem] overflow-hidden border border-border-light bg-bg-soft">
            <div className="relative rounded-[2.5rem] p-10 md:p-16 text-center">
              <div className="flex justify-center mb-6">
                <div className="p-4 bg-accent-red/10 rounded-full text-accent-red">
                  <MessageSquare className="w-8 h-8" />
                </div>
              </div>

              <h2 className="text-3xl font-bold mb-4 tracking-tight text-charcoal">
                Need a deeper dive?
              </h2>

              <p className="text-muted mb-10 max-w-md mx-auto leading-relaxed">
                If your question is not listed, our team is ready to help you
                navigate your specific requirements.
              </p>

              <I18nLink
                href="/contact"
                className="group/btn inline-flex items-center gap-2 bg-accent-red text-white px-6 md:px-8 py-3 md:py-4 rounded-full font-bold hover:opacity-90 transition-all duration-300 active:scale-95 whitespace-nowrap"
              >
                Contact Highland Group
                <ArrowRight className="w-4 h-4 group-hover/btn:translate-x-1 transition-transform" />
              </I18nLink>
            </div>
          </div>
        </section>
      </main>
    </>
  );
}